/**
 * TelemetryService — client-side event collection and batch dispatch.
 *
 * Captures every user interaction, SSE lifecycle event, TTS playback,
 * store transition, and unhandled error into a local buffer. Periodically
 * flushes (every FLUSH_INTERVAL_MS or on page unload) to the backend
 * `POST /api/debate/telemetry` endpoint.
 *
 * Design constraints:
 *   • Fire-and-forget: never blocks the UI thread.
 *   • Lossy under pressure: if the buffer exceeds MAX_BUFFER, oldest
 *     events are evicted (ring-buffer semantics).
 *   • Graceful degradation: network failures are silently swallowed;
 *     telemetry must never break the product.
 *   • Subject is stamped server-side from the JWT — unforgeable.
 *
 * CSRF note: the regular `fetch` flush path attaches `X-Macdm-Csrf` like
 * every other mutating transport. The `sendBeacon` unload path cannot set
 * custom headers at all (browser API limitation) — those beacons will be
 * rejected with `csrf.mismatch` server-side. That's an accepted trade-off
 * given telemetry is explicitly best-effort/lossy by design; it never
 * surfaces to the user or blocks product flows.
 */
import { withCsrfHeader } from "./csrf";
import { captureToSentry } from "../observability/sentry";

// ─── Configuration ─────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 200;
const ENDPOINT = "/api/debate/telemetry";

// ─── Types ─────────────────────────────────────────────────────────────────

export type TelemetryLevel = "debug" | "info" | "warn" | "error";

export type TelemetryCategory =
  | "navigation"
  | "user_action"
  | "sse"
  | "tts"
  | "store"
  | "error"
  | "performance"
  | "debate"
  | "wizard"
  | "rag"
  | "misc";

export interface TelemetryEvent {
  session_id: string | null;
  category: TelemetryCategory;
  event_name: string;
  payload: Record<string, unknown> | null;
  level: TelemetryLevel;
  source: string;
  client_ts: string;
  duration_ms: number | null;
  error_message: string | null;
  stack_trace: string | null;
}

// ─── Buffer & State ────────────────────────────────────────────────────────

let buffer: TelemetryEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let sessionId: string | null = null;
let initialized = false;

// ─── Core API ──────────────────────────────────────────────────────────────

/**
 * Initialize the telemetry service. Call once at app boot.
 * Automatically sets up periodic flush + unload hook.
 */
export function initTelemetry(): void {
  if (initialized) return;
  initialized = true;

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  // Flush on page unload (uses sendBeacon for reliability)
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush(true);
    }
  });

  window.addEventListener("pagehide", () => flush(true));

  // Global error handlers
  window.addEventListener("error", (e) => {
    trackError("unhandled_error", e.message, e.error?.stack);
  });

  window.addEventListener("unhandledrejection", (e) => {
    const msg =
      e.reason instanceof Error ? e.reason.message : String(e.reason);
    const stack = e.reason instanceof Error ? e.reason.stack : undefined;
    trackError("unhandled_rejection", msg, stack);
  });
}

/**
 * Set the active debate session ID. All subsequent events are tagged.
 */
export function setTelemetrySession(id: string | null): void {
  sessionId = id;
}

/**
 * Track any event.
 */
export function track(
  category: TelemetryCategory,
  eventName: string,
  payload?: Record<string, unknown> | null,
  opts?: {
    level?: TelemetryLevel | undefined;
    durationMs?: number | undefined;
    errorMessage?: string | undefined;
    stackTrace?: string | undefined;
  },
): void {
  const evt: TelemetryEvent = {
    session_id: sessionId,
    category,
    event_name: eventName,
    payload: payload ?? null,
    level: opts?.level ?? "info",
    source: "frontend",
    client_ts: new Date().toISOString(),
    duration_ms: opts?.durationMs ?? null,
    error_message: opts?.errorMessage ?? null,
    stack_trace: opts?.stackTrace?.slice(0, 4000) ?? null,
  };

  if (buffer.length >= MAX_BUFFER) {
    buffer.shift();
  }
  buffer.push(evt);
}

// ─── Convenience Helpers ───────────────────────────────────────────────────

export function trackNavigation(route: string, from?: string): void {
  track("navigation", "page_view", { route, from });
}

export function trackUserAction(
  action: string,
  detail?: Record<string, unknown>,
): void {
  track("user_action", action, detail);
}

export function trackSSE(
  eventName: string,
  detail?: Record<string, unknown> | null,
  level?: TelemetryLevel,
): void {
  track("sse", eventName, detail, level ? { level } : undefined);
}

export function trackTTS(
  eventName: string,
  detail?: Record<string, unknown> | null,
  level?: TelemetryLevel,
): void {
  track("tts", eventName, detail, level ? { level } : undefined);
}

export function trackStoreTransition(
  transition: string,
  detail?: Record<string, unknown>,
): void {
  track("store", transition, detail);
}

export function trackError(
  name: string,
  message?: string,
  stack?: string,
): void {
  track("error", name, null, {
    level: "error",
    errorMessage: message ?? undefined,
    stackTrace: stack ?? undefined,
  });
  // Every existing trackError() call site (SSE, wizard, TTS, store guards,
  // global window handlers below) also reaches Sentry through this one
  // choke point — no per-call-site changes needed elsewhere.
  captureToSentry(name, message, stack);
}

export function trackPerformance(
  metric: string,
  durationMs: number,
  detail?: Record<string, unknown>,
): void {
  track("performance", metric, detail, { durationMs });
}

export function trackDebate(
  eventName: string,
  detail?: Record<string, unknown> | null,
  level?: TelemetryLevel,
): void {
  track("debate", eventName, detail, level ? { level } : undefined);
}

export function trackWizard(
  eventName: string,
  detail?: Record<string, unknown> | null,
  level?: TelemetryLevel,
): void {
  track("wizard", eventName, detail, level ? { level } : undefined);
}

// ─── Flush Logic ───────────────────────────────────────────────────────────

/**
 * Flush the buffer to the backend. Uses sendBeacon on unload for
 * best-effort delivery when the page is closing.
 */
export function flush(useBeacon = false): void {
  if (buffer.length === 0) return;

  const batch = buffer.splice(0, MAX_BUFFER);
  const body = JSON.stringify({ events: batch });

  if (useBeacon && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(ENDPOINT, blob);
    return;
  }

  fetch(ENDPOINT, {
    method: "POST",
    headers: withCsrfHeader({ "Content-Type": "application/json" }),
    credentials: "include",
    body,
    keepalive: true,
  }).catch(() => {
    // On transient network failure, attempt to restore events to the
    // buffer so they can be included in the next flush cycle.
    // We silently discard if the buffer is already at capacity — telemetry
    // is lossy by design and must never affect product behaviour.
    if (buffer.length < MAX_BUFFER) {
      const restored = batch.slice(0, MAX_BUFFER - buffer.length);
      buffer.unshift(...restored);
    }
  });
}

/**
 * Destroy the service (for tests / HMR cleanup).
 */
export function destroyTelemetry(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush(true);
  buffer = [];
  initialized = false;
}
